import { useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, InfoIcon } from 'lucide-react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';

import Select from 'components/select';
import NumberField from 'components/number-field';
import ButtonGroup from 'components/button-group';
import RadioSelectItem from 'components/radio-select-item';
import ScenarioInputsDrawer from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-inputs-drawer';

import { SummaryType } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { ScenarioHarvestTargetType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { usePostProcessMapper } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-post-process-mapper';

export type ResultTabValue = 'left' | 'right';

interface ResultTabHeaderProps {
  selectedTab: ResultTabValue;
  onTabChange: (tab: ResultTabValue) => void;
}

type PostProcessUpdateForm = {
  summaryType: SummaryType;
  week: string;
  weight: string;
  mortalityProfileId: string;
};

const ResultTabHeader: React.FC<ResultTabHeaderProps> = ({ selectedTab, onTabChange }) => {
  const theme = useTheme();
  const { buildPostProcessOverride } = usePostProcessMapper();
  const { r2ScenarioQuery: { data: scenario } } = useR2ScenarioState();
  const { setEditedFishGroups, mortalityProfilesQuery, updateScenarioPostProcessQuery, getScenarioPostProcessQuery: { data: postProcessData } } = useScenarioResultState();

  const { minAllowedWeek, maxAllowedWeek, minAllowedWeight, maxAllowedWeight, scenarioDefaults, fishGroupSummaries } = postProcessData ?? {};
  const { harvestTarget, harvestTargetType, mortalityProfileId } = scenarioDefaults ?? {};

  const [openForm, setOpenForm] = useState<boolean>(false);

  const {
    reset,
    watch,
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<PostProcessUpdateForm>({
    mode: 'onChange',
    defaultValues: {
      summaryType: SummaryType.Week,
      week: '',
      weight: '',
      mortalityProfileId: '',
    },
  });

  const selectedSummaryType = watch('summaryType');

  useEffect(() => {
    if (!scenarioDefaults) return;
    const isWeek = harvestTargetType === ScenarioHarvestTargetType.WEEK.toLocaleLowerCase();
    reset({
      summaryType: isWeek ? SummaryType.Week : SummaryType.Weight,
      week: isWeek ? String(harvestTarget ?? '') : '',
      weight: isWeek ? '' : String(harvestTarget ?? ''),
      mortalityProfileId: String(mortalityProfileId ?? ''),
    });
  }, [scenarioDefaults]);

  // #region HANDLERS
  const handleUpdate = handleSubmit((values) => {
    const targetType = values.summaryType === SummaryType.Week;
    
    const body = buildPostProcessOverride({
      useScenarioDefaults: true,
      harvestTargetType: targetType ? ScenarioHarvestTargetType.WEEK : ScenarioHarvestTargetType.WEIGHT,
      harvestTarget: targetType ? Number(values.week) : Number(values.weight),
      mortalityProfileId: Number(values.mortalityProfileId),
    }, fishGroupSummaries, scenarioDefaults);

    if(!body) return;

    updateScenarioPostProcessQuery.mutate(body, {
      onSuccess: () => setEditedFishGroups(new Set()),
    });
  });
  // #endregion HANDLERS

  return (
    <Box flexDirection={'column'} display={'flex'} gap={4}>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <ButtonGroup
          value={selectedTab}
          size="large"
          buttons={[
            {
              label: 'Site',
              value: 'left',
              sx: {
                '&.MuiToggleButton-root.Mui-selected': {
                  backgroundColor: theme.palette.primary.o75,
                },
              },
              state: selectedTab === 'left',
              handleButtonClick: () => {
                onTabChange('left');
              },
            },
            {
              label: 'Fish groups',
              value: 'fish-groups',
              sx: {
                whiteSpace: 'nowrap',
                width: 'fit-content',
                '&.MuiToggleButton-root.Mui-selected': {
                  backgroundColor: theme.palette.primary.o75,
                  whiteSpace: 'nowrap',
                },
              },
              state: selectedTab === 'right',
              handleButtonClick: () => {
                onTabChange('right');
              },
            },
          ]}
          handleClick={() => { }}
        />
        <Box display={'flex'} flexDirection={'row'} textAlign={'center'} gap={2} alignItems={'flex-end'}>
          {!openForm ? (
            <Button
              sx={{
                color: theme.palette.common.white,
                '&:hover': {
                  color: (theme) => theme.palette.common.white,
                },
              }}
              variant="text"
              onClick={() => setOpenForm(!openForm)}>
              Harvest / Mortality <ChevronDown color={theme.palette.common.white} />
            </Button>
          ) : (
            <>
              <Box flexDirection={'column'}>
                <Box display={'flex'} justifyContent={'flex-end'}>
                  <Button
                    sx={{
                      color: theme.palette.common.white,
                      '&:hover': {
                        color: (theme) => theme.palette.common.white,
                      },
                    }}
                    variant="text"
                    onClick={() => setOpenForm(!openForm)}>
                    Harvest / Mortality
                    <ChevronUp color={theme.palette.common.white} />
                  </Button>
                </Box>
              </Box>
            </>
          )}
          <Box>
            <ScenarioInputsDrawer />
          </Box>
        </Box>
      </Box>
      {openForm && (
        <Box maxWidth={1150} display={'flex'} flexDirection={'column'} gap={4}>
          <Box display={'flex'} flexDirection={'row'} gap={4} alignItems={'center'}>
            <Select
              labelId="mode-label"
              id="mode-select"
              label={'Harvest Target'}
              controller={{ name: 'summaryType', control }}
              sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}>
              {Object.values(SummaryType).map((summaryType) => (
                <RadioSelectItem key={summaryType} value={summaryType}>
                  <Typography variant="body1" textTransform={'capitalize'}>
                    {summaryType}
                  </Typography>
                </RadioSelectItem>
              ))}
            </Select>
            {selectedSummaryType === SummaryType.Week ? (
              <NumberField
                key={'week'}
                endAdornment={'Weeks'}
                label={`Enter ${minAllowedWeek} - ${maxAllowedWeek}`}
                controller={{
                  name: 'week',
                  control,
                  rules: {
                    required: true,
                    min: minAllowedWeek,
                    max: maxAllowedWeek,
                  },
                }}
              />
            ) : (
              <NumberField
                key={'weight'}
                endAdornment={'g'}
                label={`Enter ${minAllowedWeight} - ${maxAllowedWeight}`}
                controller={{
                  name: 'weight',
                  control,
                  rules: {
                    required: true,
                    min: minAllowedWeight,
                    max: maxAllowedWeight,
                  },
                }}
              />
            )}
            <Select
              fullWidth
              sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}
              label="Mortality profile"
              controller={{ name: 'mortalityProfileId', control }}>
              {mortalityProfilesQuery.data?.map((mor) => (
                <RadioSelectItem key={mor.id} value={mor.id}>
                  {mor.name}
                </RadioSelectItem>
              ))}
            </Select>
            <Button onClick={handleUpdate} disabled={updateScenarioPostProcessQuery.isPending || !isValid || !scenario}>
              {updateScenarioPostProcessQuery.isPending ? <CircularProgress size={16} color="inherit" /> : 'Update'}
            </Button>
          </Box>
          <Box display={'flex'} gap={4}>
            <InfoIcon /> This will update all fish groups on site and override changes made on fish groups
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ResultTabHeader);
